import React, { useContext, useEffect, useMemo, useState } from 'react'

import { Drawer, Modal } from 'antd'
import moment from 'moment'
import { bindActionCreators } from 'redux'
import { getTranslate } from 'react-localize-redux'
import { connect, useDispatch } from 'react-redux'

import GlobalQuotaCard from './GlobalQuotaCard'
import QuotaCard from './QuotaCard'
import { DEFAULT_DRAWER_WIDTH } from '../utils/Constants'
import GlobalPlacesDrawer from './GlobalPlacesDrawer'
import PauseCard from './PauseCard'
import { createNewPauseQuota, createNewQuota } from '../../../../utils/entities/entityFactory'
import { getUser } from '../../../../reducers/UserReducer'
import { removeQuota, setReduxQuota, updateReduxQuota } from '../../../../reducers/QuotasReducer/actions'
import { createPauseQuota, deletePauseQuota, getPauseQuotas, updatePauseQuota } from '../../../../reducers/PauseQuotasReducer/PauseQuotasReducer'
import { getAppStates } from '../../../../reducers/AppStates/actions'
import { InstitutionOptions } from '../../../../utils/constants'
import { GlobalContext } from '../../../../Providers/GlobalProvider'
import { getSectorSectionGroups, getSectorSections } from '../../../../utils/api/sector'
import Loading from '../../../../HOC/Loading'
import { isObject } from '../../../../utils'
import { createQuota, updateQuota } from '../../../../utils/api/quota'
import { Quota } from '../../../../utils/entities/quota'
import { onError, onSuccess, resolvePromises } from '../../../../utils/apiHelper'

import '../../../../assets/smart-table.scss'
import '../../../../assets/quotas.scss'
import { InstitutionContext } from '../../../../Providers/InstitutionProvider'

const DEFAULT_TITLE = 'Attribute places'
const PAUSES_TITLE = 'Attribute places to pauses'
const DEFAULT_CONFIG = { initialTotal: 0, total: 0, remaining: 0, startDate: moment().startOf('day'), endDate: moment().endOf('day') }

const mapStateToProps = state => ({
  pauseActivated: getAppStates(state).pauseActivated,
  pauseQuotas: getPauseQuotas(state),
  t: getTranslate(state.locale),
  user: getUser(state.getUser)
})

const mapDispatchToProps = dispatch => ({
  createPauseQuota: bindActionCreators(createPauseQuota, dispatch),
  deletePauseQuota: bindActionCreators(deletePauseQuota, dispatch),
  removeQuota: bindActionCreators(removeQuota, dispatch),
  updatePauseQuota: bindActionCreators(updatePauseQuota, dispatch)
})

const PlacesManagingDrawer = ({
  data, pauseActivated, pauseQuotas, pauses, sector, user, visible, onClose, t, createPauseQuota, deletePauseQuota,
  removeQuota, updatePauseQuota
}) => {
  const gc = useContext(GlobalContext)
  const { institutionOptions } = useContext(InstitutionContext)

  const dispatch = useDispatch()

  const [config, setConfig] = useState({ ...DEFAULT_CONFIG })
  const [newPlaces, setNewPlaces] = useState(null)
  const [openGlobalModal, setOpenGlobalModal] = useState(false)
  const [displayWarning, setDisplayWarning] = useState(false)
  const [sectorData, setSectorData] = useState({ sections: [], sectionGroups: [], loading: true })

  const options = useMemo(() => {
    const options = {
      autoIncrement: false,
      pauseUsed: false
    }

    institutionOptions.forEach(o => {
      if (o.optionType.type === InstitutionOptions.QUOTA_PER_PAUSE) {
        options.pauseUsed = true
      } else if (o.optionType.type === InstitutionOptions.UPDATE_GLOBAL_QUOTA) {
        options.autoIncrement = o.optionEnabled
      }
    })

    return options
  }, [institutionOptions])

  const quotas = useMemo(() => {
    let quotas = []

    if (pauseActivated) {
      if (isObject(pauses)) {
        quotas = pauses.quotas.map(q => pauseQuotas[q]).filter(q => !!q)
      }
    } else if (data) {
      quotas = data.quotas.filter(q => q.section || q.sectionGroup)
    }

    return quotas
  }, [pauseActivated, pauseQuotas, data, pauses])
  const sections = useMemo(() => {
    let sections = sectorData.sections.length === 0 ? gc.sections : sectorData.sections

    if (sectorData.sectionGroups.length > 0 && !pauseActivated) {
      const unavailableSections = sectorData.sectionGroups.reduce((acc, group) => {
        group.sections.forEach(section => {
          acc[section.id] = true
        })

        return acc
      }, {})

      sections = sections.filter(s => !unavailableSections[s.id])
    }

    return sections
  }, [gc.sections, sectorData.sections, sectorData.sectionGroups, pauseActivated])

  useEffect(() => {
    if (data) {
      configureHeaderAndDates(data, pauses, pauseActivated)
    }
  }, [data, pauses, pauseActivated])

  useEffect(() => {
    if (sector) {
      const promises = []

      promises.push(getSectorSections(user, sector).then(({ data }) => {
        setSectorData(prev => ({ ...prev, sections: data }))
      }))
      promises.push(getSectorSectionGroups(user, sector).then(({ data }) => {
        setSectorData(prev => ({ ...prev, sectionGroups: data }))
      }))

      resolvePromises(promises).then(() => setSectorData(prev => ({ ...prev, loading: false })))
    }
  }, [sector, user])

  const configureHeaderAndDates = (data, pauses, pauseActivated) => {
    let newValues = decrementRemainingByState(
      data,
      pauses,
      {
        ...DEFAULT_CONFIG, initialTotal: data.total, total: data.total, remaining: data.total, startDate: null, endDate: null
      },
      pauseActivated
    )

    if (data.quotas) {
      data.quotas.forEach(quota => {
        if (!quota.section && !quota.sectionGroup) {
          newValues = getGlobalValues(newValues, quota)
        }
      })
    }

    if (newValues.startDate === null) {
      newValues.startDate = moment().startOf('day')
    }

    if (newValues.endDate === null) {
      newValues.endDate = moment().endOf('day')
    }

    setConfig(newValues)
  }

  const decrementRemainingByState = (data, pauses, newValues, pauseActivated) => {
    if (pauseActivated) {
      if (pauses && pauses.total) {
        newValues.remaining -= pauses.total
      }

      return newValues
    }

    if (data.quotas) {
      data.quotas.forEach(q => {
        if (q.section || q.sectionGroup) {
          newValues.remaining -= q.total
        }
      })
    }

    return newValues
  }

  const getGlobalValues = (values, quota) => {
    const { startDate, endDate } = values
    const quotaStartDate = moment(quota.startDate)
    const quotaEndDate = moment(quota.endDate)

    return {
      ...values,
      startDate: startDate === null || startDate < quotaStartDate ? quotaStartDate : startDate,
      endDate: endDate === null || endDate > quotaEndDate ? quotaEndDate : endDate
    }
  }

  const createNewPlaces = () => {
    const newPlaces = pauseActivated ? createNewPauseQuota(sector, config.startDate, config.endDate) : createNewQuota(sector, config.startDate, config.endDate)

    setNewPlaces(newPlaces)
  }

  const updatePlace = (quota) => {
    updateQuota(user, new Quota(quota)).then(({ data }) => {
      dispatch(updateReduxQuota(data))

      onSuccess(t('quota_places.update.success'))
    }).catch(() => {
      onError(t('quota_places.update.error'))
    })
  }

  const handleOnClose = () => {
    setConfig({ ...DEFAULT_CONFIG })
    setNewPlaces(null)

    onClose()
  }

  const handlePlacesSave = quota => {
    if (pauseActivated) {
      createPauseQuota(quota, user)
    } else {
      createQuota(user, new Quota(quota)).then(({ data }) => {
        dispatch(setReduxQuota(data))

        onSuccess(t('quota_places.create.success'))
      }).catch(() => {
        onError(t('quota_places.create.error'))
      })
    }

    setNewPlaces(null)
    postModification()
  }

  const handlePlacesUpdate = quota => {
    const method = pauseActivated ? updatePauseQuota : updatePlace

    method(quota, user)
    postModification()
  }

  const postModification = () => {
    if (options.autoIncrement && (config.total !== config.initialTotal || config.remaining < 0)) {
      balancingQuotas()
    } else if (config.remaining < 0) {
      setDisplayWarning(true)
    }
  }

  const balancingQuotas = () => {
    const globalQuota = data.quotas.find(q => !q.section)

    if (globalQuota) {
      updatePlace({ ...globalQuota, total: config.total - config.remaining })
    }
  }

  const handlePlacesSuppression = quota => {
    const method = pauseActivated ? deletePauseQuota : removeQuota

    method(quota, user)
  }

  const incrementRemaining = value => {
    if (options.autoIncrement && config.remaining === 0 && (config.total - value) >= config.initialTotal) {
      setConfig({ ...config, total: config.total - value })
    } else {
      setConfig({ ...config, remaining: config.remaining + value })
    }
  }

  const Card = useMemo(() => pauseActivated ? PauseCard : QuotaCard, [pauseActivated])

  return (
    <div>
      <Drawer
        title={(
          <div className='places-creater-drawer-title'>
            {sector &&
              <h4 style={{ textAlign: 'center', margin: '0px' }}>
                {t(pauseActivated ? PAUSES_TITLE : DEFAULT_TITLE) + ': ' + sector.name}
              </h4>}
          </div>
        )}
        width={DEFAULT_DRAWER_WIDTH}
        onClose={handleOnClose}
        visible={visible}
      >
        {sectorData.loading && (<Loading />)}
        <div className='places-managing-drawer-body'>
          <GlobalQuotaCard
            warning={(config.total !== config.initialTotal || config.remaining < 0)}
            warningMessage={t('global_quota_card.warning.' + (options.autoIncrement ? 'auto' : 'manual'))}
            pauseActivated={pauseActivated}
            total={config.total}
            remaining={config.remaining}
            startDate={config.startDate}
            endDate={config.endDate}
            pauseUsed={options.pauseUsed}
            onPlacesAllocated={createNewPlaces}
            onGlobalPlacesDisplayed={() => setOpenGlobalModal(true)}
            allocateButtonDisable={!!newPlaces}
          />
          <div className={newPlaces || quotas.length ? 'places-managing-drawer-inner-body' : ''}>
            {newPlaces && (
              <Card
                data={newPlaces}
                sections={sections}
                sectionGroups={sectorData.sectionGroups}
                onDelete={() => setNewPlaces(null)}
                onSave={handlePlacesSave}
                onPlacesChanged={incrementRemaining}
              />
            )}
            {quotas.map(q => (
              <Card
                key={q.id}
                data={q}
                sections={sections}
                sectionGroups={sectorData.sectionGroups}
                onDelete={handlePlacesSuppression}
                onSave={handlePlacesUpdate}
                onPlacesChanged={incrementRemaining}
              />
            ))}
          </div>
        </div>
      </Drawer>
      <GlobalPlacesDrawer
        visible={openGlobalModal}
        quotas={data?.quotas ?? []}
        onClose={() => setOpenGlobalModal(false)}
      />
      <Modal
        visible={displayWarning}
        title={t('quota_warning_modal.title')}
        onOk={() => {
          balancingQuotas()
          setDisplayWarning(false)
        }}
        onCancel={() => setDisplayWarning(false)}
        okText={t('modal.ok_button.balancing')}
        cancelText={t('modal.cancel_button')}
      >
        {t('quota_warning_modal.body')}
      </Modal>
    </div>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(PlacesManagingDrawer)
